import { articleEditPath, articleListPath, articleNewPath } from '@/common/app/path'
import { WidgetBadgeChoice, WidgetChoice } from '@/common/form/choice'
import { MediaAction, MediaActionStatus } from '@/common/model/media/mediaAction'

import { BadgeType } from '@/components/view/atoms/badge/Badge'

// -----------------------------------------------
// タブ
// -----------------------------------------------

// 推奨アクション管理ページの上位タブ（一覧／ロードマップ）
export const MEDIA_ACTION_VIEW_TABS = [
  { key: 'list', label: '一覧' },
  { key: 'roadmap', label: 'ロードマップ' }
] as const

export type MediaActionViewTabKey = (typeof MEDIA_ACTION_VIEW_TABS)[number]['key']

// 「一覧」タブ内の状態フィルタ（5種。ロードマップ・レポートは含まない）
export const ACTION_STATUS_FILTERS = [
  { key: 'suggested', label: '提案中' },
  { key: 'in_progress', label: '進行中' },
  { key: 'auto_executed', label: '自動実行済み' },
  { key: 'done', label: '完了' },
  { key: 'rejected', label: '却下' }
] as const

export type ActionTabKey = (typeof ACTION_STATUS_FILTERS)[number]['key']

// ダッシュボードのサマリー件数表示用（ロードマップ含む6種。レポートは廃止のため対象外）
export const ACTION_SUMMARY_COUNT_ITEMS = [
  { key: 'suggested', label: '提案中' },
  { key: 'roadmap', label: 'ロードマップ' },
  { key: 'in_progress', label: '進行中' },
  { key: 'auto_executed', label: '自動実行済み' },
  { key: 'done', label: '完了' },
  { key: 'rejected', label: '却下' }
] as const

export const actionStatusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case MediaActionStatus.SUGGESTED:
      return '提案中'
    case MediaActionStatus.IN_PROGRESS:
      return '進行中'
    case MediaActionStatus.AUTO_EXECUTED:
      return '自動実行済み'
    case MediaActionStatus.DONE:
      return '完了'
    case MediaActionStatus.REJECTED:
      return '却下'
    default:
      return status ?? '-'
  }
}

export const actionStatusBadgeType = (status: string | null | undefined): BadgeType => {
  switch (status) {
    case MediaActionStatus.IN_PROGRESS:
      return BadgeType.CAUTION
    case MediaActionStatus.AUTO_EXECUTED:
      return BadgeType.EMPHASIS
    case MediaActionStatus.DONE:
      return BadgeType.SUCCESS
    case MediaActionStatus.REJECTED:
      return BadgeType.CRITICAL
    default:
      return BadgeType.DEFAULT
  }
}

// ステータスの遷移ルール（旧「進行する」「完了」「却下」「提案中に復活する」に対応）。
// あるステータスから変更可能なステータスのみを列挙する。
export const ACTION_STATUS_TRANSITIONS: Record<MediaActionStatus, MediaActionStatus[]> = {
  [MediaActionStatus.SUGGESTED]: [
    MediaActionStatus.IN_PROGRESS, // 進行する
    MediaActionStatus.DONE, // 完了
    MediaActionStatus.REJECTED // 却下
  ],
  [MediaActionStatus.IN_PROGRESS]: [
    MediaActionStatus.DONE, // 完了
    MediaActionStatus.REJECTED // 却下
  ],
  [MediaActionStatus.AUTO_EXECUTED]: [
    MediaActionStatus.DONE, // 完了
    MediaActionStatus.REJECTED // 却下
  ],
  [MediaActionStatus.DONE]: [],
  [MediaActionStatus.REJECTED]: [
    MediaActionStatus.SUGGESTED // 提案中に復活する
  ]
}

const STATUS_ORDER: MediaActionStatus[] = [
  MediaActionStatus.SUGGESTED,
  MediaActionStatus.IN_PROGRESS,
  MediaActionStatus.AUTO_EXECUTED,
  MediaActionStatus.DONE,
  MediaActionStatus.REJECTED
]

// DisclosureSelector 用のステータス選択肢。現在ステータスから遷移できないステータスは disabled にする
// （現在ステータス自身は選択中として有効のまま表示する）。
export const actionStatusChoices = (
  current: MediaActionStatus
): WidgetBadgeChoice<MediaActionStatus>[] =>
  STATUS_ORDER.map((status) => ({
    name: actionStatusLabel(status),
    value: status,
    badgeType: actionStatusBadgeType(status),
    disabled: status !== current && !ACTION_STATUS_TRANSITIONS[current].includes(status)
  }))

// -----------------------------------------------
// アクション種別・ソース分析
// -----------------------------------------------

export const ACTION_TYPE_LABEL: Record<string, string> = {
  create_article: 'AI記事作成',
  rewrite_article: 'リライト',
  add_internal_link: '内部リンク追加',
  resolve_cannibalization: 'カニバリ解消',
  strategic_action: '戦略アクション',
  investigate: '調査タスク'
}

export const actionTypeLabel = (type: string | null | undefined): string =>
  (type && ACTION_TYPE_LABEL[type]) || type || '-'

// アクション種別 → 実行対象画面のパス（ルートの正はフロント。旧 target_screen には依存しない）
const ACTION_TARGET_PATH: Record<string, ((siteId: number) => string) | undefined> = {
  create_article: (siteId) => articleNewPath(siteId)
}

// 「今すぐ実行」の対象画面へ遷移するURLを組み立てる（prefilled_params をクエリに展開）
// 遷移先は action_type から解決する（DB保存の target_screen は廃止ルートを指す場合があるため使わない）
export const buildActionTargetUrl = (action: MediaAction, siteId: number): string | null => {
  // リライト：対象記事が自社記事案として特定できれば編集画面へ深リンクし
  // AIリライトモーダルを自動起動（openAiRewrite）。特定できなければ記事一覧へ誘導する。
  if (action.action_type === 'rewrite_article') {
    const proposalId = action.prefilled_params?.article_proposal_id
    return typeof proposalId === 'number'
      ? `${articleEditPath(siteId, proposalId, { openAiRewrite: true })}`
      : articleListPath(siteId)
  }

  const base = ACTION_TARGET_PATH[action.action_type]?.(siteId)
  if (!base) return null
  const params = new URLSearchParams()
  Object.entries(action.prefilled_params ?? {}).forEach(([k, v]) => {
    if (v == null) return
    params.set(k, typeof v === 'string' ? v : JSON.stringify(v))
  })
  params.set('action_id', String(action.id))
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

// 「今すぐ実行」ボタンを表示するアクション種別（新規記事作成・リライトのみ）
export const EXECUTABLE_NOW_ACTION_TYPES = ['create_article', 'rewrite_article'] as const

// 自動化設定の対象アクションタイプ
export const ACTION_TYPES_FOR_SETTINGS = [
  { key: 'create_article', label: 'AI記事作成' },
  { key: 'rewrite_article', label: 'リライト' },
  { key: 'add_internal_link', label: '内部リンク追加' },
  { key: 'resolve_cannibalization', label: 'カニバリ解消' },
  { key: 'strategic_action', label: '戦略アクション' }
] as const

export const SOURCE_ANALYSIS_LABEL: Record<string, string> = {
  content_gap: 'コンテンツギャップ',
  cannibalization: 'カニバリ分析',
  link_structure: '内部リンク',
  competitor: '競合分析'
}

export const sourceAnalysisLabel = (type: string | null | undefined): string =>
  (type && SOURCE_ANALYSIS_LABEL[type]) || '—'

// 分類ソース（AI分解の判定方法）
export const CLASSIFICATION_SOURCE_LABEL: Record<string, string> = {
  rule_classifier: 'ルール判定',
  ai_classifier: 'AI判定',
  fallback: '要調査',
  manual: '手動'
}

// -----------------------------------------------
// フィルタ・ソート
// -----------------------------------------------

export const ACTION_SORT_CHOICES: WidgetChoice<string>[] = [
  { name: '優先度順', value: 'priority' },
  { name: '提案日（新しい順）', value: 'suggested_at' },
  { name: 'アクション種別順', value: 'action_type' },
  { name: '実施予定日順', value: 'scheduled_date' }
]

export const ACTION_TYPE_FILTER_CHOICES: WidgetChoice<string>[] = Object.entries(
  ACTION_TYPE_LABEL
).map(([value, name]) => ({ name, value }))

export const SOURCE_FILTER_CHOICES: WidgetChoice<string>[] = Object.entries(
  SOURCE_ANALYSIS_LABEL
).map(([value, name]) => ({ name, value }))

// -----------------------------------------------
// ロードマップ
// -----------------------------------------------

// 同KW群のクールダウン期間（日）
export const COOLDOWN_DAYS = 14

// 実行ログの phase 表示名
export const execPhaseLabel = (phase: string | null | undefined): string => {
  switch (phase) {
    case 'starting':
      return '開始準備中...'
    case 'keyword_analysis_running':
      return '① キーワード分析 実行中...'
    case 'keyword_analysis_done':
      return '① キーワード分析 完了'
    case 'article_generate_running':
      return '② AI記事生成 実行中...'
    case 'completed':
      return '完了'
    case 'error':
      return 'エラー'
    default:
      return phase ?? ''
  }
}
